import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  LifeBuoy,
  Loader2,
  MessageSquare,
  RefreshCw,
  Send,
  Star,
} from "lucide-react";
import {
  createSupportTicket,
  fetchSupportTickets,
  getSupportAccess,
  markSupportTicketRead,
  rateSupportTicket,
  replyToSupportTicket,
  ticketCategories,
} from "../../Support/supportTickets";

const initialForm = {
  category: "payment",
  subject: "",
  message: "",
};

const ticketTabs = ["Open", "In Progress", "Resolved"];

const formatTicketDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "resolved") return "bg-green-50 text-green-700 ring-green-100";
  if (key === "in progress") return "bg-blue-50 text-blue-700 ring-blue-100";
  return "bg-amber-50 text-amber-700 ring-amber-100";
};

const getTicketId = (ticket) => ticket?._id || ticket?.id || ticket?.ticketNumber;

const getCategoryLabel = (category) => {
  if (category === "generation-support") return "Study & Practice Help";
  return (
    ticketCategories.find((item) => item.value === category || item.label === category)?.label ||
    category ||
    "Support"
  );
};

const getCategoryDescription = (category) =>
  ticketCategories.find((item) => item.value === category || item.label === category)?.description || "";

const TicketMessage = ({ message }) => {
  const isAdmin = message.authorType === "admin";

  return (
    <div className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[86%] rounded-md px-4 py-3 text-sm shadow-sm ${
          isAdmin ? "border border-slate-200 bg-white text-gray-700" : "bg-purple-700 text-white"
        }`}
      >
        <p className={`text-xs font-bold ${isAdmin ? "text-purple-700" : "text-purple-100"}`}>
          {message.authorName || (isAdmin ? "PrepNexis Support" : "You")}
        </p>
        <p className="mt-1 leading-6">{message.message}</p>
      </div>
    </div>
  );
};

export default function SupportTicketPanel({ dashboardData }) {
  const [access, setAccess] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [form, setForm] = useState(initialForm);
  const [reply, setReply] = useState("");
  const [activeTab, setActiveTab] = useState("Open");
  const [rating, setRating] = useState({ score: 0, comment: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRatingSubmitting, setIsRatingSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const ticketCounts = useMemo(
    () =>
      ticketTabs.reduce((counts, status) => {
        counts[status] = tickets.filter((ticket) => (ticket.status || "Open") === status).length;
        return counts;
      }, {}),
    [tickets]
  );

  const filteredTickets = useMemo(
    () => tickets.filter((ticket) => (ticket.status || "Open") === activeTab),
    [activeTab, tickets]
  );

  const selectedTicket = useMemo(
    () => filteredTickets.find((ticket) => getTicketId(ticket) === selectedTicketId) || filteredTickets[0],
    [filteredTickets, selectedTicketId]
  );

  const selectedTicketIdentity = getTicketId(selectedTicket);
  const isSelectedResolved = selectedTicket?.status === "Resolved";

  const loadTickets = useCallback(async (userEmail) => {
    setIsLoadingTickets(true);
    setError("");

    try {
      const ticketList = await fetchSupportTickets({ userEmail });
      setTickets(ticketList);
      setSelectedTicketId((current) => current || getTicketId(ticketList[0]) || "");
    } catch (fetchError) {
      setError(fetchError.message || "Unable to load your tickets");
    } finally {
      setIsLoadingTickets(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      setIsCheckingAccess(true);
      setError("");

      try {
        const accessResult = await getSupportAccess(dashboardData);
        if (!isMounted) return;

        setAccess(accessResult);
        if (accessResult.eligible) {
          await loadTickets(accessResult.user.email);
        }
      } catch (accessError) {
        if (isMounted) {
          setError(accessError.message || "Unable to check support access");
        }
      } finally {
        if (isMounted) {
          setIsCheckingAccess(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [dashboardData, loadTickets]);

  useEffect(() => {
    if (!access?.eligible) return undefined;

    const refreshTimer = setInterval(() => {
      loadTickets(access.user.email);
    }, 30000);

    return () => clearInterval(refreshTimer);
  }, [access, loadTickets]);

  useEffect(() => {
    if (!filteredTickets.length) {
      if (selectedTicketId) setSelectedTicketId("");
      return;
    }

    const selectedTicketExists = filteredTickets.some((ticket) => getTicketId(ticket) === selectedTicketId);
    if (!selectedTicketExists) {
      setSelectedTicketId(getTicketId(filteredTickets[0]));
    }
  }, [filteredTickets, selectedTicketId]);

  useEffect(() => {
    setRating({
      score: selectedTicket?.rating?.score || 0,
      comment: selectedTicket?.rating?.comment || "",
    });
  }, [selectedTicketIdentity, selectedTicket?.rating?.score, selectedTicket?.rating?.comment]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setNotice("");
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setNotice("");
    setError("");

    if (!form.subject.trim() || !form.message.trim()) {
      setError("Please add a subject and message before creating a ticket.");
      return;
    }

    setIsSubmitting(true);

    try {
      const ticket = await createSupportTicket({
        ...form,
        subject: form.subject.trim(),
        message: form.message.trim(),
        userName: access.user.name,
        userEmail: access.user.email,
        userRole: access.role,
        subscriptionStatus: access.subscriptionStatus,
      });

      setTickets((current) => [ticket, ...current]);
      setActiveTab("Open");
      setSelectedTicketId(getTicketId(ticket));
      setForm(initialForm);
      setNotice("Ticket created successfully.");
    } catch (submitError) {
      setError(submitError.message || "Unable to create support ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!selectedTicket || !reply.trim()) return;
    if (isSelectedResolved) {
      setError("Resolved tickets are closed for replies.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setNotice("");

    try {
      const updatedTicket = await replyToSupportTicket(getTicketId(selectedTicket), reply.trim());
      setTickets((current) =>
        current.map((ticket) => (getTicketId(ticket) === getTicketId(updatedTicket) ? updatedTicket : ticket))
      );
      setReply("");
      setNotice("Reply sent.");
    } catch (replyError) {
      setError(replyError.message || "Unable to send reply");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTicket = (updatedTicket) => {
    setTickets((current) =>
      current.map((ticket) => (getTicketId(ticket) === getTicketId(updatedTicket) ? updatedTicket : ticket))
    );
  };

  const handleSelectTicket = async (ticket) => {
    const ticketId = getTicketId(ticket);
    setSelectedTicketId(ticketId);

    if (!ticket.unreadForUser) return;

    try {
      const updatedTicket = await markSupportTicketRead(ticketId);
      updateTicket(updatedTicket);
    } catch (readError) {
      console.error("Unable to mark ticket as read:", readError);
    }
  };

  const handleRatingSubmit = async () => {
    if (!selectedTicket || !rating.score) {
      setError("Please select a rating before submitting.");
      return;
    }

    setIsRatingSubmitting(true);
    setError("");
    setNotice("");

    try {
      const updatedTicket = await rateSupportTicket(getTicketId(selectedTicket), rating);
      updateTicket(updatedTicket);
      setNotice("Thanks for rating this support ticket.");
    } catch (ratingError) {
      setError(ratingError.message || "Unable to submit rating");
    } finally {
      setIsRatingSubmitting(false);
    }
  };

  if (isCheckingAccess) {
    return null;
  }

  if (!access?.eligible) {
    return null;
  }

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-5 flex flex-col gap-4 border-b border-slate-100 pb-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
            <LifeBuoy className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">Support tickets</p>
            <h2 className="text-2xl font-extrabold text-gray-900">Create a ticket</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={() => loadTickets(access.user.email)}
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-white px-3 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoadingTickets ? "animate-spin" : ""}`} aria-hidden="true" />
          Refresh
        </button>
      </div>

      {(error || notice) && (
        <div
          className={`mb-5 flex items-start gap-2 rounded-md border px-4 py-3 text-sm font-semibold ${
            error
              ? "border-red-100 bg-red-50 text-red-700"
              : "border-green-100 bg-green-50 text-green-700"
          }`}
        >
          {error ? <AlertCircle className="mt-0.5 h-4 w-4" /> : <CheckCircle2 className="mt-0.5 h-4 w-4" />}
          <span>{error || notice}</span>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(280px,420px),1fr]">
        <form onSubmit={handleSubmit} className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="grid gap-4">
            <div>
              <label htmlFor="support-category" className="block text-xs font-semibold uppercase text-gray-500">
                Category
              </label>
              <select
                id="support-category"
                value={form.category}
                onChange={handleChange("category")}
                className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-gray-800 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
              >
                {ticketCategories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-sm text-gray-500">{getCategoryDescription(form.category)}</p>
            </div>

            <div>
              <label htmlFor="support-subject" className="block text-xs font-semibold uppercase text-gray-500">
                Subject
              </label>
              <input
                id="support-subject"
                value={form.subject}
                onChange={handleChange("subject")}
                maxLength={160}
                className="mt-2 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                placeholder="Example: I cannot access speaking module"
              />
            </div>

            <div>
              <label htmlFor="support-message" className="block text-xs font-semibold uppercase text-gray-500">
                Message
              </label>
              <textarea
                id="support-message"
                value={form.message}
                onChange={handleChange("message")}
                rows={6}
                className="mt-2 w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                placeholder="Tell us what happened and which page or module is affected."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-purple-700 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Submit Ticket
            </button>
          </div>
        </form>

        <div className="grid gap-4 lg:grid-cols-[minmax(240px,330px),1fr]">
          <div className="rounded-md border border-slate-200">
            <div className="border-b border-slate-100 p-4">
              <p className="text-sm font-bold text-gray-900">Your tickets</p>
              <p className="text-xs text-gray-500">{tickets.length} total</p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {ticketTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`rounded-md px-2.5 py-2 text-xs font-bold transition ${
                      activeTab === tab
                        ? "bg-purple-700 text-white"
                        : "bg-slate-50 text-gray-600 ring-1 ring-slate-200 hover:bg-white"
                    }`}
                  >
                    <span className="block truncate">{tab}</span>
                    <span className={activeTab === tab ? "text-purple-100" : "text-gray-400"}>
                      {ticketCounts[tab] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-[480px] overflow-y-auto p-3">
              {isLoadingTickets ? (
                <div className="flex items-center gap-2 rounded-md bg-slate-50 p-4 text-sm font-semibold text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-700" />
                  Loading tickets
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="rounded-md bg-slate-50 p-4 text-center text-sm text-gray-500">
                  No {activeTab.toLowerCase()} tickets.
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTickets.map((ticket) => {
                    const ticketId = getTicketId(ticket);
                    const isSelected = getTicketId(selectedTicket) === ticketId;
                    const unreadCount = Number(ticket.unreadForUser) || 0;

                    return (
                      <button
                        key={ticketId}
                        type="button"
                        onClick={() => handleSelectTicket(ticket)}
                        className={`w-full rounded-md border p-3 text-left transition ${
                          isSelected
                            ? "border-purple-200 bg-purple-50"
                            : "border-slate-200 bg-white hover:border-purple-100 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-bold text-gray-900">{ticket.subject}</span>
                          <div className="flex shrink-0 items-center gap-2">
                            {unreadCount > 0 && (
                              <span className="grid h-6 min-w-6 place-items-center rounded-full bg-rose-600 px-1.5 text-xs font-extrabold text-white">
                                {unreadCount}
                              </span>
                            )}
                            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ring-1 ${statusClass(ticket.status)}`}>
                              {ticket.status || "Open"}
                            </span>
                          </div>
                        </div>
                        <p className="mt-2 truncate text-xs font-semibold text-purple-700">
                          {ticket.ticketNumber || "New ticket"} - {getCategoryLabel(ticket.category)}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">{formatTicketDate(ticket.updatedAt || ticket.createdAt)}</p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-[420px] rounded-md border border-slate-200 bg-slate-50 p-4">
            {selectedTicket ? (
              <div className="flex h-full flex-col">
                <div className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase text-purple-700">
                      {selectedTicket.ticketNumber || "Ticket"}
                    </p>
                    <h3 className="mt-1 text-lg font-extrabold text-gray-900">{selectedTicket.subject}</h3>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold">
                      <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-slate-200">
                        {getCategoryLabel(selectedTicket.category)}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 ring-1 ${statusClass(selectedTicket.status)}`}>
                        {selectedTicket.status || "Open"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                    <Clock3 className="h-4 w-4" />
                    {formatTicketDate(selectedTicket.updatedAt || selectedTicket.createdAt)}
                  </div>
                </div>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
                  {(selectedTicket.messages?.length
                    ? selectedTicket.messages
                    : [{ authorType: "user", authorName: access.user.name, message: selectedTicket.message }]
                  ).map((message, index) => (
                    <TicketMessage key={message._id || message.id || index} message={message} />
                  ))}
                </div>

                <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row">
                  {isSelectedResolved ? (
                    <div className="w-full rounded-md border border-green-100 bg-green-50 p-4">
                      <div className="flex items-start gap-2 text-sm font-semibold text-green-700">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>This ticket is resolved. Replies are closed.</span>
                      </div>
                      <div className="mt-4 rounded-md bg-white p-3 ring-1 ring-green-100">
                        <p className="text-xs font-bold uppercase text-gray-500">Rate this support experience</p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {[1, 2, 3, 4, 5].map((score) => (
                            <button
                              key={score}
                              type="button"
                              onClick={() => setRating((current) => ({ ...current, score }))}
                              className={`grid h-9 w-9 place-items-center rounded-md ring-1 transition ${
                                rating.score >= score
                                  ? "bg-amber-50 text-amber-500 ring-amber-100"
                                  : "bg-white text-gray-300 ring-slate-200 hover:text-amber-400"
                              }`}
                              aria-label={`Rate ${score} out of 5`}
                            >
                              <Star className={`h-4 w-4 ${rating.score >= score ? "fill-current" : ""}`} />
                            </button>
                          ))}
                        </div>
                        <textarea
                          value={rating.comment}
                          onChange={(event) => setRating((current) => ({ ...current, comment: event.target.value }))}
                          rows={3}
                          className="mt-3 w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                          placeholder="Optional feedback"
                        />
                        <button
                          type="button"
                          onClick={handleRatingSubmit}
                          disabled={isRatingSubmitting || !rating.score}
                          className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-purple-700 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isRatingSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                          {selectedTicket.rating?.score ? "Update Rating" : "Submit Rating"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={reply}
                        onChange={(event) => setReply(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") handleReply();
                        }}
                        className="h-11 flex-1 rounded-md border border-slate-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                        placeholder="Reply to this ticket"
                      />
                      <button
                        type="button"
                        onClick={handleReply}
                        disabled={isSubmitting || !reply.trim()}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-gray-900 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <MessageSquare className="h-4 w-4" />
                        Reply
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex min-h-[360px] flex-col items-center justify-center text-center text-sm text-gray-500">
                <MessageSquare className="mb-3 h-8 w-8 text-purple-300" />
                Create a ticket to start a support conversation.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
